import { useEffect, useState } from "react";
import { get, type ContractRecord } from "../api";

/**
 * Which contract you are looking at.
 *
 * A contract administrator carries a portfolio — several schemes for one
 * client, or several clients — and every surface merged them, so a
 * deadline gave no clue which contract it was on and there was no way to
 * look at one.
 *
 * It hides itself when there is only one contract, because a chooser with
 * a single option is furniture. The pilot has exactly one today, so most
 * readers will never see this.
 *
 * The selection is not a security boundary and is not treated as one: the
 * server narrows what a principal was already entitled to, and a
 * reference they are not scoped to is refused there rather than filtered
 * here.
 */
export function ContractPicker({ chosen, onChoose }: {
  chosen: string;
  onChoose: (reference: string) => void;
}) {
  const [contracts, setContracts] = useState<ContractRecord[] | null>(null);

  useEffect(() => {
    get<{ contracts: ContractRecord[] }>("/contracts")
      .then((d) => setContracts(d.contracts))
      .catch(() => setContracts([]));
  }, []);

  if (!contracts || contracts.length < 2) return null;

  const live = contracts.reduce((n, c) => n + c.live_clocks, 0);
  const current = contracts.find((c) => c.reference === chosen);

  return (
    <label className="picker">
      <span className="picker__k">Contract</span>
      <select className="picker__select" value={chosen}
        onChange={(e) => onChoose(e.target.value)}>
        <option value="all">
          All {contracts.length} contracts · {live} live
        </option>
        {contracts.map((c) => (
          <option key={c.id} value={c.reference}>
            {c.reference} — {c.project || "no project name"} · {c.live_clocks} live
          </option>
        ))}
      </select>
      {current?.client && (
        <span className="picker__client" title="The Employer under this contract">
          {current.client}
        </span>
      )}
    </label>
  );
}

/**
 * Remembers the chosen contract across screens and reloads.
 *
 * Switching contract and then opening the board should not silently put
 * you back on everything — the choice is about which piece of work you
 * are doing, not which page you are on.
 */
const KEY = "neev.contract";

export function useChosenContract(): [string, (reference: string) => void] {
  const [chosen, setChosen] = useState<string>(() => {
    try {
      return window.localStorage.getItem(KEY) || "all";
    } catch {
      return "all";
    }
  });

  const choose = (reference: string) => {
    setChosen(reference);
    try {
      window.localStorage.setItem(KEY, reference);
    } catch {
      /* a browser refusing storage is not a reason to refuse the switch */
    }
  };

  return [chosen, choose];
}
